import type {
  LoopMode,
  PlaybackDirection,
} from '../../project/ProjectDocument.js'

export type PlaybackPhase = 'moving' | 'holdingOrder' | 'holdingChaos' | 'completed'
export type TravelDirection = 1 | -1

export interface PlaybackState {
  readonly progress: number
  readonly travelDirection: TravelDirection
  readonly phase: PlaybackPhase
  readonly holdRemaining: number
  readonly completed: boolean
}

export interface MutablePlaybackState {
  progress: number
  travelDirection: TravelDirection
  phase: PlaybackPhase
  holdRemaining: number
  completed: boolean
}

export interface PlaybackAdvanceInput {
  deltaSeconds: number
  /** Order-progress units per second; the editor maps its speed control to this once. */
  progressPerSecond: number
  direction: PlaybackDirection
  loopMode: LoopMode
  holdAtOrder: number
  holdAtChaos: number
}

function assertPlaybackDirection(direction: PlaybackDirection): void {
  switch (direction) {
    case 'forward':
    case 'reverse':
    case 'pingPong':
      return
    default:
      throw new RangeError(`Unknown playback direction: ${String(direction)}`)
  }
}

function assertLoopMode(loopMode: LoopMode): void {
  switch (loopMode) {
    case 'loop':
    case 'once':
      return
    default:
      throw new RangeError(`Unknown playback loop mode: ${String(loopMode)}`)
  }
}

function assertPlaybackPhase(phase: PlaybackPhase): void {
  switch (phase) {
    case 'moving':
    case 'holdingOrder':
    case 'holdingChaos':
    case 'completed':
      return
    default:
      throw new RangeError(`Unknown playback phase: ${String(phase)}`)
  }
}

function isSafeDuration(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER
}

export function validatePlaybackState(state: PlaybackState): void {
  assertPlaybackPhase(state.phase)

  if (!Number.isFinite(state.progress) || state.progress < 0 || state.progress > 1) {
    throw new RangeError('Playback progress must be finite and within 0…1')
  }
  if (state.travelDirection !== 1 && state.travelDirection !== -1) {
    throw new RangeError('Playback travel direction must be 1 or -1')
  }
  if (!isSafeDuration(state.holdRemaining)) {
    throw new RangeError('Playback hold remaining must be finite, non-negative, and safe')
  }
  if (typeof state.completed !== 'boolean') {
    throw new TypeError('Playback completed must be boolean')
  }
  if ((state.phase === 'completed') !== state.completed) {
    throw new RangeError('Playback phase and completed flag are contradictory')
  }
  switch (state.phase) {
    case 'moving':
      if (state.holdRemaining !== 0) {
        throw new RangeError('Moving playback state cannot retain a hold')
      }
      break
    case 'holdingOrder':
      if (state.progress !== 1 || state.holdRemaining <= 0) {
        throw new RangeError('Order hold state requires order progress and a positive hold')
      }
      break
    case 'holdingChaos':
      if (state.progress !== 0 || state.holdRemaining <= 0) {
        throw new RangeError('Chaos hold state requires chaos progress and a positive hold')
      }
      break
    case 'completed':
      if ((state.progress !== 0 && state.progress !== 1) || state.holdRemaining !== 0) {
        throw new RangeError('Completed playback state requires an endpoint and no hold')
      }
      break
  }
}

function validatePlayback(state: PlaybackState, input: PlaybackAdvanceInput): void {
  assertPlaybackDirection(input.direction)
  assertLoopMode(input.loopMode)
  validatePlaybackState(state)
  if (!isSafeDuration(input.deltaSeconds)) {
    throw new RangeError('Playback delta must be finite, non-negative, and safe')
  }
  if (
    !Number.isFinite(input.progressPerSecond)
    || input.progressPerSecond <= 0
    || input.progressPerSecond > Number.MAX_SAFE_INTEGER
  ) {
    throw new RangeError('Playback progress speed must be finite, positive, and safe')
  }
  if (!isSafeDuration(input.holdAtOrder)) {
    throw new RangeError('Order hold must be finite, non-negative, and safe')
  }
  if (!isSafeDuration(input.holdAtChaos)) {
    throw new RangeError('Chaos hold must be finite, non-negative, and safe')
  }
}

/**
 * Advance playback using elapsed time only. No timers or allocations are used;
 * the result is written to caller-owned state and input may alias output.
 *
 * A zero delta is a validated structural no-op: state is copied byte-for-byte
 * to a distinct output (or left untouched when aliased). Requested direction
 * changes and zero-duration boundary transitions begin with the first positive
 * delta, which makes paused editor frames stable and predictable.
 */
export function advancePlayback(
  state: PlaybackState,
  input: PlaybackAdvanceInput,
  out: MutablePlaybackState,
): void {
  // Validate before touching the output so failures are transaction-safe.
  validatePlayback(state, input)

  if (input.deltaSeconds === 0) {
    if (state !== out) {
      out.progress = state.progress
      out.travelDirection = state.travelDirection
      out.phase = state.phase
      out.holdRemaining = state.holdRemaining
      out.completed = state.completed
    }
    return
  }

  let progress = state.progress
  let travelDirection = input.direction === 'forward'
    ? 1 as const
    : input.direction === 'reverse'
      ? -1 as const
      : state.travelDirection
  let phase = state.phase
  let holdRemaining = state.holdRemaining
  let completed = state.completed
  let remaining = input.deltaSeconds

  while (!completed) {
    // Once a loop reaches its canonical start, whole cycles can be discarded in
    // O(1). This keeps resume-after-background and exported large deltas cheap.
    if (input.loopMode === 'loop' && phase === 'moving' && holdRemaining === 0) {
      const atForwardStart = input.direction === 'forward'
        && travelDirection === 1 && progress === 0
      const atReverseStart = input.direction === 'reverse'
        && travelDirection === -1 && progress === 1
      const atPingPongStart = input.direction === 'pingPong'
        && travelDirection === 1 && progress === 0
      if (atForwardStart || atReverseStart || atPingPongStart) {
        const travelDuration = (input.direction === 'pingPong' ? 2 : 1)
          / input.progressPerSecond
        const cycleDuration = travelDuration + input.holdAtOrder + input.holdAtChaos
        if (remaining >= cycleDuration) {
          const cycles = Math.floor(remaining / cycleDuration)
          remaining -= cycles * cycleDuration
          if (remaining < Number.EPSILON * cycleDuration) remaining = 0
        }
      }
    }

    if (phase === 'holdingOrder' || phase === 'holdingChaos') {
      if (holdRemaining > 0) {
        if (remaining < holdRemaining || remaining === 0) {
          holdRemaining -= remaining
          remaining = 0
          break
        }
        remaining -= holdRemaining
        holdRemaining = 0
      }

      const heldAtOrder = phase === 'holdingOrder'
      phase = 'moving'

      if (input.direction === 'forward') {
        if (heldAtOrder) {
          if (input.loopMode === 'once') {
            completed = true
            phase = 'completed'
          } else {
            progress = 0
            phase = 'holdingChaos'
            holdRemaining = input.holdAtChaos
          }
        } else {
          travelDirection = 1
        }
      } else if (input.direction === 'reverse') {
        if (!heldAtOrder) {
          if (input.loopMode === 'once') {
            completed = true
            phase = 'completed'
          } else {
            progress = 1
            phase = 'holdingOrder'
            holdRemaining = input.holdAtOrder
          }
        } else {
          travelDirection = -1
        }
      } else if (heldAtOrder) {
        travelDirection = -1
      } else if (input.loopMode === 'once' && travelDirection === -1) {
        completed = true
        phase = 'completed'
      } else {
        travelDirection = 1
      }
      continue
    }

    if (phase === 'completed') {
      completed = true
      break
    }

    const target = travelDirection === 1 ? 1 : 0
    const distance = Math.abs(target - progress)
    if (distance > 0) {
      const travelSeconds = distance / input.progressPerSecond
      if (remaining < travelSeconds || remaining === 0) {
        progress += travelDirection * input.progressPerSecond * remaining
        remaining = 0
        break
      }
      progress = target
      remaining -= travelSeconds
    }

    if (target === 1) {
      phase = 'holdingOrder'
      holdRemaining = input.holdAtOrder
    } else {
      phase = 'holdingChaos'
      holdRemaining = input.holdAtChaos
    }
  }

  out.progress = progress
  out.travelDirection = travelDirection
  out.phase = phase
  out.holdRemaining = completed ? 0 : holdRemaining
  out.completed = completed
}
